import React from 'react';
import { X, FileText, CheckCircle, Scale } from 'lucide-react';

const BNCoauthorshipModal = ({ isOpen, onClose, onAccept, workTitle, userName, userDetails }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-2xl">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <Scale className="w-8 h-8" />
              <div>
                <h2 className="text-2xl font-bold">Termo Opcional de Adesão</h2>
                <p className="text-blue-100">Registro Coletivo na Biblioteca Nacional</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 text-sm">
          
          {/* Destaque Importante */}
          <div className="bg-green-50 border-2 border-green-300 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-green-900 mb-2">✅ REGISTRO TOTALMENTE OPCIONAL E GRATUITO</h3>
                <ul className="text-sm text-green-800 space-y-1">
                  <li>✅ Coautoria técnica 1% apenas para legitimidade do registro</li>
                  <li>✅ Você mantém 99% dos direitos + 100% dos ganhos econômicos</li>
                  <li>✅ Plataforma renuncia a toda participação financeira</li>
                  <li>✅ Registro na BN a cada 120 dias - GRATUITO</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Partes */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-3">PARTES</h3>
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <div>
                <p className="font-semibold text-gray-900">MINHA COMPOSIÇÃO AUTORAL</p>
                <p className="text-gray-700 text-xs">CNPJ: 63.854.898/0001-85</p>
                <p className="text-gray-700 text-xs">Av. Santos Dumont, 6061, Sala 515, Lauro de Freitas/BA, CEP: 42.712-740</p>
                <p className="text-gray-700 text-xs">E-mail: sacminhascomposicoes@gmail.com</p>
              </div>
              <div className="border-t pt-2">
                <p className="font-semibold text-gray-900">AUTOR(A)</p>
                <p className="text-gray-700">Nome: {userName}</p>
                {userDetails?.cpf_cnpj && <p className="text-gray-700">CPF/CNPJ: {userDetails.cpf_cnpj}</p>}
                {userDetails?.nationality && <p className="text-gray-700">Nacionalidade: {userDetails.nationality}</p>}
                {userDetails?.profession && <p className="text-gray-700">Profissão: {userDetails.profession}</p>}
                {userDetails?.address && (
                  <p className="text-gray-700">
                    Endereço: {userDetails.address}
                    {userDetails.city && `, ${userDetails.city}`}
                    {userDetails.state && `/${userDetails.state}`}
                    {userDetails.zip_code && ` - CEP: ${userDetails.zip_code}`}
                  </p>
                )}
                <p className="text-gray-700 mt-2 font-medium">Obra: {workTitle}</p>
              </div>
            </div>
          </div>

          {/* Artigo 1 */}
          <div>
            <h4 className="font-bold text-gray-900 mb-2">Artigo 1º – Do Objeto</h4>
            <p className="text-gray-700">
              O presente Termo tem por objeto regulamentar a <strong>adesão opcional</strong> do autor ao serviço de registro coletivo de obras musicais junto à Biblioteca Nacional (BN), a ser promovido pela plataforma MINHA COMPOSIÇÃO, mediante <strong>solicitação expressa do autor</strong>.
            </p>
          </div>

          {/* Artigo 2 */}
          <div>
            <h4 className="font-bold text-gray-900 mb-2">Artigo 2º – Da Natureza Opcional do Registro</h4>
            <p className="text-gray-700">
              O registro na Biblioteca Nacional é <strong>facultativo</strong> e ocorrerá apenas se o autor, de forma livre e consciente, manifestar interesse e consentimento expresso para que sua obra seja incluída no procedimento coletivo de registro realizado pela MINHA COMPOSIÇÃO.
            </p>
          </div>

          {/* Artigo 3 */}
          <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4">
            <h4 className="font-bold text-gray-900 mb-2">Artigo 3º – Da Coautoria Técnica e Legitimidade para o Registro</h4>
            <p className="text-gray-700 mb-2">
              Para fins de viabilização técnica e jurídica do registro coletivo perante a Biblioteca Nacional, o autor autoriza que a MINHA COMPOSIÇÃO figure como <strong>coautora técnica da obra, com participação simbólica de 1%</strong> (um por cento) da titularidade.
            </p>
            <p className="text-gray-700 mb-2">
              Essa participação tem <strong>caráter meramente formal e instrumental</strong>, sendo limitada à finalidade exclusiva de legitimar o registro, sem qualquer envolvimento criativo, artístico ou intelectual da plataforma na elaboração da obra.
            </p>
            <p className="text-gray-700">
              A coautoria técnica <strong>não gera vínculo associativo, societário ou contratual</strong> entre as partes, restringindo-se à finalidade declarada neste Termo.
            </p>
          </div>

          {/* Artigo 4 */}
          <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4">
            <h4 className="font-bold text-gray-900 mb-2">Artigo 4º – Da Renúncia de Direitos Econômicos</h4>
            <p className="text-gray-700 mb-2">
              A MINHA COMPOSIÇÃO declara, de forma <strong>irrevogável e irretratável</strong>, que renuncia a qualquer direito patrimonial ou ganho financeiro sobre a obra, incluindo, mas não se limitando a:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              <li>royalties;</li>
              <li>licenciamento;</li>
              <li>execução pública;</li>
              <li>sincronização;</li>
              <li>distribuição digital ou física;</li>
              <li>qualquer outra forma de exploração econômica.</li>
            </ul>
            <p className="text-gray-700 mt-2 font-semibold">
              O autor majoritário permanecerá, portanto, como <strong>único titular dos direitos econômicos e patrimoniais da obra</strong>.
            </p>
          </div>

          {/* Artigo 5 */}
          <div>
            <h4 className="font-bold text-gray-900 mb-2">Artigo 5º – Da Responsabilidade do Autor</h4>
            <p className="text-gray-700">
              O autor declara ser o legítimo criador da obra e assume integral responsabilidade civil e penal por seu conteúdo, garantindo que a composição não infringe direitos de terceiros e não constitui plágio, cópia ou reprodução indevida de obras pré-existentes.
            </p>
          </div>

          {/* Artigo 6 */}
          <div>
            <h4 className="font-bold text-gray-900 mb-2">Artigo 6º – Do Procedimento de Registro</h4>
            <p className="text-gray-700 mb-2">
              O registro será efetuado pela MINHA COMPOSIÇÃO em nome do autor e da plataforma, conforme exigências técnicas e normativas da Biblioteca Nacional.
            </p>
            <p className="text-gray-700 mb-2">
              O autor reconhece que a inclusão da MINHA COMPOSIÇÃO como coautora técnica é condição indispensável para viabilizar o registro coletivo.
            </p>
            <p className="text-gray-700">
              A eventual recusa do autor em aderir a este modelo não prejudica o registro individual de sua obra, que poderá ser realizado diretamente pelo mesmo junto à Biblioteca Nacional, caso deseje.
            </p>
          </div>

          {/* Artigo 7 */}
          <div>
            <h4 className="font-bold text-gray-900 mb-2">Artigo 7º – Da Coautoria Técnica e Limitação de Responsabilidade</h4>
            <p className="text-gray-700 mb-2">
              A MINHA COMPOSIÇÃO poderá figurar como coautora técnica da obra, conforme autorização expressa do autor, exclusivamente para fins de registro coletivo junto à Biblioteca Nacional.
            </p>
            <p className="text-gray-700 mb-2">
              Essa coautoria é <strong>formal, técnica e não criativa</strong>, não implicando em participação artística, autoral ou intelectual da plataforma.
            </p>
            <p className="text-gray-700 mb-2">
              A MINHA COMPOSIÇÃO <strong>não se responsabiliza</strong>, em nenhuma hipótese, por eventuais infrações de direitos autorais, plágios, falsidades ideológicas, declarações inverídicas ou quaisquer outras irregularidades relacionadas à autoria ou ao conteúdo da obra apresentada pelo autor.
            </p>
            <p className="text-gray-700">
              O autor responde individualmente por toda e qualquer consequência legal decorrente da obra registrada, inclusive perante terceiros e órgãos públicos.
            </p>
          </div>

          {/* Artigo 8 */}
          <div>
            <h4 className="font-bold text-gray-900 mb-2">Artigo 8º – Das Disposições Finais</h4>
            <p className="text-gray-700 mb-2">
              Este Termo entra em vigor na data de sua aceitação eletrônica pelo autor no ambiente digital da MINHA COMPOSIÇÃO.
            </p>
            <p className="text-gray-700 mb-2">
              O aceite eletrônico possui a mesma validade jurídica da assinatura manuscrita, nos termos da <strong>Lei nº 14.063/2020</strong>.
            </p>
            <p className="text-gray-700">
              O foro eleito para dirimir quaisquer controvérsias oriundas deste instrumento é o da <strong>Comarca de Lauro de Freitas/BA</strong>, com renúncia expressa a qualquer outro, por mais privilegiado que seja.
            </p>
          </div>

          {/* Assinatura */}
          <div className="border-t-2 border-gray-300 pt-4">
            <p className="text-xs text-gray-600 mb-3">
              Lauro de Freitas – BA, {new Date().toLocaleDateString('pt-BR')}
            </p>
            <div className="space-y-2 text-xs text-gray-700">
              <p><strong>MINHA COMPOSIÇÃO AUTORAL</strong></p>
              <p>CNPJ: 63.854.898/0001-85</p>
              <p>E-mail: sacminhascomposicoes@gmail.com</p>
              <p className="mt-3"><strong>AUTOR(A):</strong> {userName}</p>
              <p className="text-xs text-gray-500">
                Assinatura Eletrônica válida conforme Lei nº 14.063/2020
              </p>
            </div>
          </div>

          {/* Botões */}
          <div className="flex gap-4 pt-4 border-t">
            <button
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-800 py-4 px-6 rounded-xl font-semibold hover:bg-gray-300 transition-colors"
            >
              ❌ Não Aceitar (Continuar sem BN)
            </button>
            <button
              onClick={onAccept}
              className="flex-1 bg-gradient-to-r from-green-600 to-blue-600 text-white py-4 px-6 rounded-xl font-semibold hover:from-green-700 hover:to-blue-700 transition-all shadow-lg"
            >
              ✅ Li, Compreendi e Aceito o Termo
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BNCoauthorshipModal;
